import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import InviteUsersDialogFormFields from '../InviteUsersDialogFormFields';
import { inviteUser } from '../../../../services/UserService';
import { queryKeys } from '../../../../react-query/constants';

interface Props {
    submitForm: boolean;
    setSubmitForm: Dispatch<SetStateAction<boolean>>;
    handleClose: () => void;
}

const InviteUsersDialogForm: FC<Props> = ({ submitForm, setSubmitForm, handleClose }) => {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const formRef = useRef<HTMLFormElement>(null);

    const FormSchema = z.object({
        firstName: z.string().trim().min(1, 'required field').max(50, 'Maximum length is 50 characters'),
        lastName: z.string().trim().min(1, 'required field').max(50, 'Maximum length is 50 characters'),
        email: z
            .string()
            .min(1, 'required field')
            .max(50, 'Maximum length is 50 characters')
            .email('invalid email'),
        userType: z.string().min(1, 'required field'),
    });

    type FormSchemaType = z.infer<typeof FormSchema>;

    const methods = useForm<FormSchemaType>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            userType: '',
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const inviteMutation = useMutation(
        (formValues: FormSchemaType) =>
            inviteUser({
                ...formValues,
                // @ts-ignore
                authorities: [formValues.userType],
            }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([queryKeys.users]);
                enqueueSnackbar('User invited successfully', { variant: 'success' });
                handleClose();
            },
            onSettled: () => {
                setIsSubmitting(false);
                setSubmitForm(false);
            },
        },
    );

    const onSubmit: SubmitHandler<FormSchemaType> = async (formValues: FormSchemaType) => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await inviteMutation.mutateAsync(formValues);
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (submitForm && formRef && !isSubmitting) {
            formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    }, [setSubmitForm, submitForm, isSubmitting]);

    return (
        <FormProvider {...methods}>
            <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
                <InviteUsersDialogFormFields />
            </form>
        </FormProvider>
    );
};
export default InviteUsersDialogForm;
